import { useMutation, useQuery } from '@tanstack/react-query';
import type { LoginResponse } from '@octery/shared';
import { api, ApiClientError } from '@/lib/api';
import { navigate, useCurrentPath } from '@/app/navigation';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type InviteInfo = {
  email: string;
  role: 'admin' | 'viewer';
  companyId: string;
  companyName: string;
  expiresAt: string;
};

type AcceptInviteResponse = LoginResponse;

export function InvitePage() {
  const path = useCurrentPath();
  const token = path.match(/^\/invite\/([^/]+)$/)?.[1] || '';
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAuth = useAuthStore((s) => s.setAuth);
  const user = useAuthStore((s) => s.user);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteInfo>(`/api/auth/invite/${token}`),
    enabled: Boolean(token),
  });

  const acceptMutation = useMutation({
    mutationFn: () => api.post<AcceptInviteResponse>(`/api/auth/invite/${token}/accept`),
    onSuccess: (response) => {
      setAuth(response.user);
      navigate('/');
    },
  });

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid invite link</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const goToRegister = () => {
    const email = encodeURIComponent(data?.email || '');
    navigate(`/register?invite=${encodeURIComponent(token)}&email=${email}`);
  };

  const goToLogin = () => {
    const email = encodeURIComponent(data?.email || '');
    navigate(`/login?invite=${encodeURIComponent(token)}&email=${email}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Company invitation</CardTitle>
          <CardDescription>
            Join a team on Octery using your invitation link
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Loading invite…</p>}

          {error && (
            <p className="text-sm text-destructive">
              {error instanceof ApiClientError ? error.message : 'Invite is invalid or expired'}
            </p>
          )}

          {data && (
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">Company:</span> {data.companyName}</p>
              <p><span className="text-muted-foreground">Role:</span> {data.role}</p>
              <p><span className="text-muted-foreground">Invited email:</span> {data.email}</p>
            </div>
          )}

          {data && !isAuthenticated && (
            <div className="space-y-2">
              <Button className="w-full" onClick={goToRegister}>
                Create account and join
              </Button>
              <Button variant="outline" className="w-full" onClick={goToLogin}>
                I already have an account
              </Button>
            </div>
          )}

          {data && isAuthenticated && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Signed in as {user?.email}. To accept invite, it must match {data.email}.
              </p>
              <Button
                className="w-full"
                onClick={() => acceptMutation.mutate()}
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? 'Accepting…' : 'Accept invitation'}
              </Button>
              {acceptMutation.isError && (
                <p className="text-sm text-destructive">
                  {acceptMutation.error instanceof ApiClientError
                    ? acceptMutation.error.message
                    : 'Failed to accept invitation'}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
